import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Slider } from 'antd';
import IconFont from '$/components/IconFont';
import NextImage from '$/components/NextImage';
import VideoCom from '$/components/VideoCom';
import { useLanguageStore } from '$/store/languageStore';
import { FileTypeEnum, getVideoCoverUrl } from '$/utils/upload';
import { formateVideoTimeNoHour } from '$/components/VideoCom';
import useAIEnhanceStore, { setPreviewImageIndex, updateBatchImage } from '@/store/useAIEnhanceStore';
import styles from './index.module.scss';

/**
 * 渲染 AI 变高清批量大图预览。
 */
export default function BatchImagePreview() {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const hideVolumeTimerRef = useRef<number | null>(null);
  const imageInnerRef = useRef<HTMLDivElement | null>(null);
  const volumeRef = useRef<HTMLDivElement | null>(null);
  const [showVolume, setShowVolume] = useState(false);
  const [videoControlsBottom, setVideoControlsBottom] = useState(16);
  const batchImages = useAIEnhanceStore((state) => state.batchImages);
  const previewImageIndex = useAIEnhanceStore((state) => state.previewImageIndex);
  const singleCompareIng = useAIEnhanceStore((state) => state.singleCompareIng);

  const currentImage = previewImageIndex !== null ? batchImages[previewImageIndex] : null;
  const isPreviewVideo = currentImage?.type === FileTypeEnum.Video;
  const hasPrev = previewImageIndex !== null && previewImageIndex > 0;
  const hasNext = previewImageIndex !== null && previewImageIndex < batchImages.length - 1;

  const handlePrev = useCallback(() => {
    if (hasPrev && previewImageIndex !== null) {
      setPreviewImageIndex(previewImageIndex - 1);
    }
  }, [hasPrev, previewImageIndex]);

  const handleNext = useCallback(() => {
    if (hasNext && previewImageIndex !== null) {
      setPreviewImageIndex(previewImageIndex + 1);
    }
  }, [hasNext, previewImageIndex]);

  const handleBack = useCallback(() => {
    setPreviewImageIndex(null);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') handlePrev();
      else if (e.key === 'ArrowRight') handleNext();
      else if (e.key === 'Escape') handleBack();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrev, handleNext, handleBack]);

  const handleMouseEnterByVolume = useCallback(() => {
    if (hideVolumeTimerRef.current) {
      window.clearTimeout(hideVolumeTimerRef.current);
    }
    setShowVolume(true);
  }, []);

  const handleMouseLeaveByVolume = useCallback(() => {
    hideVolumeTimerRef.current = window.setTimeout(() => {
      setShowVolume(false);
    }, 200);
  }, []);

  useEffect(() => () => {
    if (hideVolumeTimerRef.current) {
      window.clearTimeout(hideVolumeTimerRef.current);
    }
  }, []);

  /**
   * Safari 小高度下视频预览盒可能仍按原比例溢出视口，控制条需要向可视窗口内回收。
   */
  const updateVideoControlsBottom = useCallback(() => {
    const previewRect = imageInnerRef.current?.getBoundingClientRect();
    if (!previewRect) {
      setVideoControlsBottom(16);
      return;
    }

    const viewport = window.visualViewport;
    const viewportBottom = viewport
      ? viewport.offsetTop + viewport.height
      : window.innerHeight;
    const nextBottom = Math.max(16, Math.ceil(previewRect.bottom - viewportBottom + 16));
    setVideoControlsBottom(nextBottom);
  }, []);

  useEffect(() => {
    if (!isPreviewVideo) {
      setVideoControlsBottom(16);
      return undefined;
    }

    const animationFrameId = window.requestAnimationFrame(updateVideoControlsBottom);
    window.addEventListener('resize', updateVideoControlsBottom);
    window.visualViewport?.addEventListener('resize', updateVideoControlsBottom);
    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', updateVideoControlsBottom);
      window.visualViewport?.removeEventListener('resize', updateVideoControlsBottom);
    };
  }, [
    currentImage?.height,
    currentImage?.id,
    currentImage?.width,
    isPreviewVideo,
    updateVideoControlsBottom,
  ]);

  if (previewImageIndex === null || !currentImage) return null;

  const {
    url,
    type,
    coverUrl,
    resultUrl,
    status,
    resultImageLoading = false,
    width: imgWidth,
    height: imgHeight,
  } = currentImage;
  const displayUrl = singleCompareIng ? url : (resultUrl || url);
  const isVideo = type === FileTypeEnum.Video;
  const posterSrc = isVideo ? (coverUrl || getVideoCoverUrl(displayUrl)) : displayUrl;
  const aspectRatioStyle = imgWidth && imgHeight
    ? { aspectRatio: `${imgWidth} / ${imgHeight}` }
    : undefined;
  const showLoadingOverlay = status === 'processing'
    || (status === 'success' && !!resultUrl && resultImageLoading && !singleCompareIng);
  const videoControlsStyle = {
    '--video-controls-bottom': `${videoControlsBottom}px`,
  } as React.CSSProperties;

  const handlePreviewImageLoad = () => {
    updateBatchImage(currentImage.id, { resultImageLoading: false });
  };

  return (
    <div className={styles.container}>
      {/* 返回列表按钮 */}
      <button type="button" className={styles.backBtn} onClick={handleBack}>
        <IconFont name="chevronLeftBold" fontSize={16} className={styles.backIcon} />
        {languageStore.smartcatTranslationText((v) => v.back_to_list)}
      </button>

      {/* 图片预览区 */}
      <div className={styles.imageArea}>
        <div className={styles.imageWrapper}>
          <div
            ref={imageInnerRef}
            className={styles.imageInner}
            style={aspectRatioStyle}
          >
            {isVideo ? (
              <div
                key={currentImage.id}
                className={styles.previewVideoBox}
              >
                <VideoCom
                  volume={0.5}
                  url={displayUrl}
                  autoplay={false}
                  closeVideoClick
                  className={styles.previewVideoCom}
                  videoClassName={styles.previewVideoPlayer}
                  poster={posterSrc}
                  videoFillMode="contain"
                >
                  {({
                    toggle,
                    playing,
                    currentTime,
                    seek,
                    duration,
                    volume,
                    setPlayerVolume,
                  }) => (
                    <div
                      className={styles.videoBox}
                      style={videoControlsStyle}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        className={`${styles.playBtn} ${playing ? styles.pause : ''}`}
                        onClick={toggle}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggle();
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <IconFont
                          name={playing ? 'pauseFill' : 'playFill'}
                          fontSize={20}
                          color="rgba(255, 255, 255, 1)"
                        />
                      </div>
                      <Slider
                        className={styles.videoProgress}
                        min={0}
                        max={100}
                        value={
                          (currentTime / duration === 1
                            ? 0
                            : currentTime / duration) * 100 || 0
                        }
                        tooltip={{ formatter: null }}
                        onAfterChange={(percent: number) => {
                          const time = (percent / 100) * duration;
                          seek(time);
                        }}
                        onChange={(percent: number) => {
                          const time = (percent / 100) * duration;
                          seek(time);
                        }}
                      />
                      <div className={styles.currentTime}>
                        {formateVideoTimeNoHour(
                          currentTime / duration === 1
                            ? 0
                            : Math.round(currentTime),
                        )}
                      </div>
                      <div className={styles.duration}>
                        /
                        {formateVideoTimeNoHour(Math.round(duration))}
                      </div>
                      <div
                        className={styles.volumeBox}
                        onMouseEnter={handleMouseEnterByVolume}
                        onMouseLeave={handleMouseLeaveByVolume}
                        ref={volumeRef}
                      >
                        <div
                          className={`${styles.volumeIcon} ${!volume ? styles.mute : ''}`}
                          onClick={() => {
                            if (volume) {
                              setPlayerVolume(0);
                            } else {
                              setPlayerVolume(0.5);
                            }
                          }}
                        >
                          <IconFont
                            name={(() => {
                              if (!volume) return 'muteBold';
                              return volume > 0.5
                                ? 'volumeFullBold'
                                : 'volumeHalfBold';
                            })()}
                            fontSize={20}
                            color="rgba(255, 255, 255, 1)"
                          />
                        </div>
                        {showVolume && (
                          <div
                            className={styles.volumeSliderWrap}
                            onMouseEnter={handleMouseEnterByVolume}
                            onMouseLeave={handleMouseLeaveByVolume}
                          >
                            <Slider
                              vertical
                              min={0}
                              max={1}
                              step={0.01}
                              value={volume}
                              onChange={(value: number) => {
                                setPlayerVolume(value);
                              }}
                              tooltip={{ formatter: null }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </VideoCom>
                {resultUrl && !singleCompareIng ? (
                  <NextImage
                    src={posterSrc}
                    alt=""
                    width={1}
                    height={1}
                    sizes="1px"
                    className={styles.posterPreload}
                    onLoad={resultImageLoading ? handlePreviewImageLoad : undefined}
                  />
                ) : null}
              </div>
            ) : (
              <NextImage
                src={displayUrl}
                alt=""
                height="100%"
                width="100%"
                sizes="400px"
                className={styles.previewImage}
                onLoad={
                  resultUrl && !singleCompareIng && resultImageLoading
                    ? handlePreviewImageLoad
                    : undefined
                }
              />
            )}
            {showLoadingOverlay && (
              <div className={styles.overlay}>
                <div className={styles.overlayContent}>
                  <IconFont
                    name="loadingCircleBold"
                    fontSize={24}
                    className={styles.loadingIcon}
                  />
                  <span className={styles.overlayText}>
                    {resultImageLoading
                      ? languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_loading)
                      : languageStore.smartcatTranslationText((v) => v.processing)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 左切换按钮 */}
      <button
        type="button"
        className={`${styles.navBtn} ${styles.navLeft} ${!hasPrev ? styles.navHidden : ''}`}
        onClick={handlePrev}
        disabled={!hasPrev}
        aria-label="上一张"
      >
        <IconFont name="arrowLeftBold" fontSize={24} className={styles.navIcon} />
      </button>

      {/* 右切换按钮 */}
      <button
        type="button"
        className={`${styles.navBtn} ${styles.navRight} ${!hasNext ? styles.navHidden : ''}`}
        onClick={handleNext}
        disabled={!hasNext}
        aria-label="下一张"
      >
        <IconFont name="arrowRightBold" fontSize={24} className={styles.navIcon} />
      </button>
    </div>
  );
}
